import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Award, CheckCircle, ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import {
  quickInfo, heroBadges, audienceList, learningOutcomes, modules,
  promptBankCategories, howItWorks, whatIsIncluded, responsibleAIPoints,
  featureCards, faqs
} from '../data/aiHumanitarianCourse';

const AIHumanitarianCourse = () => {
  const [openModule, setOpenModule] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI in Humanitarian Action & Development | GIHDS";
  }, []);

  const toggleModule = (index) => setOpenModule(openModule === index ? null : index);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  return (
    <div className="min-h-screen bg-white" data-testid="ai-humanitarian-page">
      {/* Hero Section */}
      <section className="relative bg-[#FAFBFC] pt-32 pb-20 md:pt-40 md:pb-28 border-b border-[#DCE4EA]">
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#C82032]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/courses" className="inline-flex items-center gap-2 text-[#52667A] hover:text-[#163A59] text-sm font-medium mb-6 transition-colors">
            <ArrowRight size={14} className="rotate-180" />
            Back to Courses
          </Link>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C82032] mb-4">
                Short Professional Course
              </p>
              <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-[#102A43] mb-6">
                AI in Humanitarian Action & Development
              </h1>
              <p className="text-xl text-[#163A59] mb-4">
                Learn how to use AI responsibly and efficiently across NGO strategy, needs assessment, program design, donor mapping, proposal development, implementation, MEAL, reporting, and daily professional work.
              </p>
              <p className="text-[#52667A] mb-6 leading-relaxed">
                Artificial intelligence is changing how humanitarian and development professionals research, plan, write, analyze, coordinate, and report. This practical course teaches you how to use AI tools across the full NGO and humanitarian program cycle.
              </p>
              <p className="text-[#52667A] mb-8 leading-relaxed">
                You will learn how to create stronger prompts, provide the right context, improve weak AI responses, verify information, and adapt AI-generated outputs to real organizational and field situations. The course does not provide ready-made project templates. It teaches you how to use AI to create, review, improve, and adapt professional outputs repeatedly across different contexts and assignments.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link
                  to="/admissions"
                  className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                  data-testid="ai-course-enroll-btn"
                >
                  Enroll Now
                  <ArrowRight size={18} />
                </Link>
                <a
                  href="#modules"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-[#163A59] text-[#163A59] hover:bg-[#163A59] hover:text-white px-8 py-4 text-sm font-semibold tracking-wide transition-all"
                >
                  View Course Modules
                </a>
              </div>

              <div className="flex flex-wrap gap-2">
                {heroBadges.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-2 bg-white text-[#163A59] text-xs px-3 py-1.5 border border-[#DCE4EA]"
                  >
                    <CheckCircle size={14} className="text-[#C82032]" />
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Course highlights card */}
            <div className="bg-[#163A59] p-6 md:p-8 shadow-xl">
              <h2 className="font-['Source_Serif_4'] text-2xl font-bold text-white mb-6">Course Highlights</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Duration</p>
                  <p className="text-white font-semibold">4 Weeks</p>
                </div>
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Mode</p>
                  <p className="text-white font-semibold">100% Online</p>
                </div>
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Standard Fee</p>
                  <p className="text-[#FCA5A5] font-bold text-lg">USD 99</p>
                </div>
                <div className="bg-white/[0.06] border border-white/10 p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wider mb-1">GIHDS Students</p>
                  <p className="text-white font-semibold">USD 59</p>
                </div>
              </div>
              <div className="space-y-2.5 border-t border-white/15 pt-5">
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5] flex-shrink-0" />
                  <span>200+ Practical AI Prompts</span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5] flex-shrink-0" />
                  <span>Self-Paced Learning</span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5] flex-shrink-0" />
                  <span>Certificate of Completion</span>
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#FCA5A5] flex-shrink-0" />
                  <span>No Technical Experience Required</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Course Info */}
      <section className="py-16 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {quickInfo.map((item) => (
              <div key={item.label} className="bg-white p-4 sm:p-5 border border-slate-200">
                <item.icon className="w-6 h-6 text-[#163A59] mb-3" strokeWidth={1.5} />
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{item.label}</p>
                <p className="text-sm font-medium text-slate-900">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Overview */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Course Overview
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-8">
              Use AI Across the Full Humanitarian and Development Program Cycle
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 mb-6">
                AI can support far more than basic writing. When used correctly, it can help humanitarian and development professionals analyze information, organize ideas, develop projects, examine funding opportunities, prepare implementation plans, improve reports, strengthen professional communication, and reduce repetitive administrative work.
              </p>
              <p className="text-slate-600 mb-6">
                However, effective AI use requires more than entering a simple question into a chatbot. Professionals need to understand:
              </p>
              <ul className="text-slate-600 space-y-2 mb-8">
                <li>Which AI tool is appropriate for a particular task</li>
                <li>What information and context the AI requires</li>
                <li>How to structure effective prompts</li>
                <li>How to continue and improve an AI conversation</li>
                <li>How to review and verify the response</li>
                <li>How to detect generic, inaccurate, or invented information</li>
                <li>How to protect confidential and sensitive information</li>
                <li>How to adapt AI outputs to organizational, donor, and field requirements</li>
              </ul>
              <p className="text-slate-600">
                This course teaches those skills within the real working environment of NGOs, humanitarian organizations, development programs, and crisis-affected contexts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Promise */}
      <section className="py-16 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl md:text-2xl text-white leading-relaxed mb-6">
              Learn how to use AI as a practical professional assistant across the full NGO and humanitarian program cycle—from organizational planning and needs assessment to donor mapping, proposal development, implementation, MEAL, reporting, and daily productivity.
            </p>
            <p className="text-slate-300">
              The objective is not to make learners dependent on ready-made formats. The objective is to enable them to use AI independently, responsibly, and repeatedly across different tasks, projects, organizations, sectors, and countries.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              What Makes This Course Different
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              More Than a General AI Course
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              This course is specifically designed for humanitarian, development, and NGO work. It does not focus on programming, machine learning, data science, or technical AI development. It focuses on how professionals can use existing AI tools to improve real work processes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-slate-200"
              >
                <card.icon className="w-10 h-10 text-[#163A59] mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{card.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Enroll */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
                Target Audience
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Who Should Enroll?
              </h2>
              <p className="text-slate-600 mb-8">
                This course is designed for professionals, students, and organizational leaders who want to use AI more effectively in humanitarian, development, and NGO work.
              </p>
              <div className="bg-[#F1F5F8] p-6 border-l-4 border-[#163A59]">
                <p className="text-sm text-slate-600">
                  <strong>Note:</strong> No coding, programming, data science, or previous AI experience is required.
                </p>
              </div>
            </div>
            <div className="bg-[#F1F5F8] p-8">
              <div className="grid sm:grid-cols-2 gap-3">
                {audienceList.map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#163A59] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Learning Outcomes
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              What Learners Will Be Able to Do
            </h2>
            <p className="text-slate-600">By the end of the course, learners should be able to:</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {learningOutcomes.map((outcome) => (
              <div key={outcome} className="flex items-start gap-3 bg-white p-4 border border-slate-200">
                <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Modules */}
      <section id="modules" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Course Curriculum
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              Course Modules
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {modules.map((module, index) => (
              <div key={module.number} className="border border-slate-200 bg-white">
                <button
                  onClick={() => toggleModule(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F1F5F8] transition-colors"
                  data-testid={`module-toggle-${index + 1}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 bg-[#163A59] text-white flex items-center justify-center font-bold text-sm">
                      {module.number}
                    </span>
                    <span className="font-semibold text-slate-900">{module.title}</span>
                  </div>
                  {openModule === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  )}
                </button>
                {openModule === index && (
                  <div className="px-6 pb-6 border-t border-slate-100">
                    <div className="pt-6">
                      <p className="text-sm font-semibold text-slate-900 mb-2">Module Overview</p>
                      <p className="text-sm text-slate-600 mb-6">{module.overview}</p>

                      <p className="text-sm font-semibold text-slate-900 mb-2">Topics Covered</p>
                      <ul className="grid sm:grid-cols-2 gap-2 mb-6">
                        {module.topics.map((topic) => (
                          <li key={topic} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="text-[#163A59] mt-1">•</span>
                            {topic}
                          </li>
                        ))}
                      </ul>

                      {module.importantNote && (
                        <div className="bg-amber-50 border border-amber-200 p-4 mb-6">
                          <p className="text-sm text-amber-800">
                            <strong>Important:</strong> {module.importantNote}
                          </p>
                        </div>
                      )}

                      <div className="bg-[#F1F5F8] p-4">
                        <p className="text-sm font-semibold text-slate-900 mb-1">Practical Focus</p>
                        <p className="text-sm text-slate-600">{module.practicalFocus}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prompt Bank */}
      <section className="py-20 md:py-28 bg-[#163A59] border-t-4 border-[#C82032]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/90 mb-4">
              Included Resource
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-4">
              Humanitarian & Development AI Prompt Bank
            </h2>
            <p className="text-white/90 max-w-2xl mx-auto mb-2">
              More than 200 reusable prompts designed for repeated use across humanitarian, development, NGO, and professional work.
            </p>
            <p className="text-white/90 max-w-2xl mx-auto text-sm">
              The prompt bank does not replace the course lessons. The lessons teach learners how to understand, modify, combine, and improve the prompts for their own situations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {promptBankCategories.map((category) => (
              <div key={category.title} className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
                <h3 className="font-semibold text-white mb-2">{category.title}</h3>
                <p className="text-white/90 text-sm">{category.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-white/90 text-sm max-w-2xl mx-auto">
              The value of the prompt bank is not that learners copy the same prompt forever. Its value is that learners understand how to adapt each prompt to different organizations, projects, countries, sectors, donors, and professional assignments.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Learning Process
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              How You Will Learn
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {howItWorks.map((item) => (
              <div key={item.step} className="relative">
                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 bg-[#163A59] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#F1F5F8] p-6 text-center max-w-4xl mx-auto">
            <p className="text-sm text-slate-600">
              <strong>Workflow:</strong> Professional task → relevant context → structured prompt → AI response → human review → improvement → verification → final professional output
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
            What Is Included in Your Enrollment?
          </h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {whatIsIncluded.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible AI Use */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Shield className="w-12 h-12 text-[#163A59] mx-auto mb-4" />
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                AI Must Support—Not Replace—Professional Judgment
              </h2>
            </div>

            <p className="text-slate-600 mb-6 text-center">
              Humanitarian and development work involves people, rights, safety, dignity, funding, accountability, and sensitive information. AI outputs may contain errors, incomplete information, fabricated references, weak assumptions, bias, or language that does not reflect the local context.
            </p>

            <p className="text-slate-600 mb-6 text-center">Learners are therefore taught to:</p>

            <div className="grid sm:grid-cols-2 gap-3 mb-10">
              {responsibleAIPoints.map((point) => (
                <div key={point} className="flex items-start gap-3 bg-[#F1F5F8] p-4">
                  <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{point}</span>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-300 p-6 text-center">
              <p className="text-amber-800 font-semibold">
                Never submit an AI-generated output as final professional work without review, verification, contextual adaptation, and human approval.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certificate */}
      <section className="py-16 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <Award className="w-16 h-16 text-[#163A59]" strokeWidth={1} />
              <div>
                <h3 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900">Certificate of Completion</h3>
                <p className="text-slate-600">Learners who complete the required course content will receive a Certificate of Completion.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Accessible Professional AI Training
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-10">
              Course Fee
            </h2>

            <div className="inline-flex flex-col sm:flex-row gap-8 items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">Standard Fee</p>
                <p className="text-5xl font-bold text-slate-900">USD 99</p>
              </div>
              <div className="hidden sm:block h-20 w-px bg-slate-200" />
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">Current GIHDS Student Rate</p>
                <p className="text-4xl font-bold text-[#C82032]">USD 59</p>
                <p className="text-xs text-slate-500 mt-1">Available only to verified learners currently enrolled in a GIHDS program.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#163A59] mb-4">
              Common Questions
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <div key={faq.question} className="border border-slate-200 bg-white">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F1F5F8] transition-colors"
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 border-t border-slate-100">
                    <p className="text-slate-600 pt-4 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-6">
            Build Practical AI Skills for Humanitarian and Development Work
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-10">
            Learn how to select AI tools, write stronger prompts, improve AI outputs, protect sensitive information, and use AI across the full NGO and humanitarian program cycle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link
              to="/admissions"
              className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-10 py-4 text-sm font-semibold tracking-wide transition-all"
            >
              Enroll Now
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-10 py-4 text-sm font-semibold tracking-wide hover:bg-white/10 transition-all"
            >
              Contact GIHDS
            </Link>
          </div>
          <p className="text-slate-300 text-sm">
            No previous AI, coding, or technical experience is required.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AIHumanitarianCourse;
